import { ReactComponent as Tick } from "../../assets/icons/round_check.svg";
import "./Reschedule.css";
import { useMemo, useEffect, useState, useContext } from "react";
import { getOrderDetails, getOrderPrice } from "../../state_management/epics";
import {
  order,
  orderDetailsType,
  settings
} from "../../state_management/actionTypes";
import { asyncResType, useAsync } from "../../utilities/asyncFunc";
import {
  c_item,
  e_item,
  rescheduleOrder
} from "../../state_management/epics/order";
import { getTimeStartInfo } from "../../utilities/cleanerTime";
import CheckOutHeader from "../CheckOutHeader/CheckOutHeader";
import Button from "../../UI/Button/Button";
import { useParams } from "react-router-dom";
import CheckOutListReschedule from "../CheckOutList/CheckOutListReschedule";
import { connect } from "react-redux";
import { stateType } from "../../store";
import {
  calc_price_with_discount,
  convertToTime,
  dateFormat,
  getDate
} from "../../utilities/misc";
import { serviceItems } from "../../state_management/epics/settings";
import CheckOutSection from "../CheckOutTime/CheckOutSection";
import { useTimeDetails } from "../../utilities/timeDetails";
import { parseDirectOrder } from "../CheckOutPayment/usePayment";
import {
  epics_cart_items,
  getCartTotal,
  parseToCartItem
} from "../../utilities/cartUtilities";
import { json } from "../../translation_json";
import { clearCart, setEntireCart } from "../../state_management/actions";
import { cartInitialState } from "../../state_management/reducers/cart";
import { CartTotalContext, LocaleContext } from "../../AppProvider";

/**
 *
 * @param time Time to check if within order creation limits
 * @returns Boolean indicating whether or not the order can be reschedule to that time
 */
export const allowableTime = (time: string, settings: Partial<settings>) => {
  try {
    /**
     * User cannot reschedule if:
     * - date is in the past
     * - Time from now to cleaning time is less than the order_reschedul_interval
     */
    const { order_creation_before_interval } = settings;
    const windowOrderMilli = order_creation_before_interval! * 60 * 1000;

    const proceed = new Date(time).getTime() >= Date.now() + windowOrderMilli;

    return proceed;
  } catch {
    return false;
  }
};

/**
 *
 * @param time Time to check if within allowable limits
 * @returns A boolean indicating whether or not a reschedule can be made
 */
export const canReschedule = (time: string, settings: Partial<settings>) => {
  try {
    /**
     * User cannot reschedule if:
     * - date is in the past
     * - Time from now to cleaning time is less than the order_reschedul_interval
     */
    const { order_reschedule_interval } = settings;
    const windowOrderMilli = order_reschedule_interval! * 60 * 1000;
    const proceed = new Date(time).getTime() >= Date.now() + windowOrderMilli;

    return proceed;
  } catch (err) {
    console.log(err);
    return false;
  }
};

const Reschedule = ({
  settings,
  settingRes
}: {
  settings: Partial<settings>;
  settingRes: asyncResType<serviceItems, settings>;
}) => {
  const { t, locale } = useContext(LocaleContext);
  const { orderId, token } = useParams();

  const [_, setCartTotal] = useContext(CartTotalContext);
  const [orderDetails, setOrderDetails] = useState<orderDetailsType["data"]>();

  const req_gift_values = orderDetails?.gift_values || [];
  const cart = orderDetails?.services || [];
  const extras =
    orderDetails?.extra_services?.map((item) => ({
      ...item,
      price_with_discount: calc_price_with_discount(
        item.discount || 0,
        item.price
      )
    })) || [];

  type epic_cart_return_type = ReturnType<typeof epics_cart_items>;
  type epic_cart_return_type_with_duration = {
    [P in keyof epic_cart_return_type]: (epic_cart_return_type[P][0] & {
      duration: number;
    })[];
  };
  const { extra_services, gift_values, services } = epics_cart_items(
    {
      cart: cart.map((a) => ({
        ...a,
        is_upsell: false
      })),
      extras,
      gift_values: req_gift_values.map((a) => ({
        ...a,
        is_upsell: false,
        item: { price: a.value, duration: a.service.duration }
      }))
    },
    ["duration"]
  ) as epic_cart_return_type_with_duration;

  const cartCombined: {
    services: (c_item & { duration: number; parent_id: undefined })[];
    extra_services: (e_item & { duration: number })[];
    gift_values: {
      service_id: number;
      quantity: number;
      value: number;
    }[];
  } = {
    services,
    extra_services,
    gift_values
  };

  const [hasError, setHasError] = useState(false);
  const [done, setDone] = useState(false);
  const [stopProcess, setStopProcess] = useState(false);
  const [time, setTime] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [dateSelected, setDateSelected] = useState<Date>();

  const submit = async () => {
    const orderId = orderDetails?.id;
    if (orderId) {
      setIsLoading(true);
      await rescheduleOrder({ orderId, time_start: time, token })
        .then((res) => {
          setDone(true);
          setIsLoading(false);
        })
        .catch((err: any) => {
          console.log(err);
          setIsLoading(false);
        });
    }
  };

  /* Total after discount */
  const total = useMemo(() => {
    return getCartTotal({
      orderDirect: true,
      cart,
      extras,
      gift_values: req_gift_values
    });
  }, [cart, extras]);

  useEffect(() => {
    setCartTotal(total);
  }, [total]);

  const settings_requested = settingRes.called && !settingRes.loading;
  const orderDetailsRes = useAsync(
    {
      asyncFunc: getOrderDetails,
      funcParams: {
        token,
        orderId
      },
      canCall: settings_requested,
      immediate: true,
      clearOnError: true
    },
    [token, orderId, settings_requested]
  );

  useEffect(() => {
    const orderObj = orderDetailsRes.data;
    if (orderObj) {
      const canContinue = canReschedule(orderObj.data.time_start, settings);
      if (orderObj.data.status !== "confirmed") {
        setTime(undefined);
        setStopProcess(true);
      } else if (canContinue) {
        const orderDetails = orderObj.data;
        setOrderDetails({ ...orderDetails });

        const timeObj = getTimeStartInfo(orderDetails.time_start);
        const { time, date } = timeObj;
        const dateSelected = getDate(timeObj.date);
        setDateSelected(dateSelected);

        if (time && date) {
          const Time = `${date} ${time}`;
          setTime(Time);
        }
        setStopProcess(false);
      } else if (settings_requested) {
        setTime(undefined);
        setStopProcess(true);
      }
    }
  }, [orderDetailsRes.data, settings_requested]);

  useEffect(() => {
    setHasError(!!orderDetailsRes.error);
  }, [orderDetailsRes.error]);

  const couponRes = useAsync(
    {
      asyncFunc: getOrderPrice,
      funcParams: {
        order_id: orderDetails?.id,
        coupon_code: orderDetails?.["coupon code"] || null,
        ...cartCombined
      },
      immediate: true,
      clearOnError: true
    },
    [JSON.stringify(orderDetails)]
  );

  const cleaner = orderDetails
    ? `${orderDetails.cleaner.firstname} ${orderDetails.cleaner.lastname}`
    : ``;

  const hasSettings =
    settings.order_creation_before_interval !== undefined &&
    settings.order_reschedule_interval !== undefined;

  return hasSettings ? (
    <div className="reschedule_wrapper checkOutList_wrapper checkoutTimeWrapper">
      <CheckOutHeader deactivate />
      {stopProcess || hasError ? (
        <div className="reschedule_confirmed_wrapper">
          {orderDetailsRes.data &&
          orderDetailsRes.data.data.status !== "confirmed"
            ? "You cannot reschedule this booking"
            : t(
                "Bookings can be rescheduled no later than {{time}} before the cleaning takes place.",
                {
                  "{{time}}": convertToTime(
                    settings.order_creation_before_interval || 0,
                    t
                  )
                }
              )}
        </div>
      ) : done ? (
        <div className=" checkout_left check_out_successful_wrapper center fdCol">
          <div className="top center">
            <span className="suc_logo">
              <Tick className="img_div_contain" />
            </span>
            <span>
              {t("Successfully rescheduled to {{time}}", {
                "{{time}}": dateFormat(time || "", locale, true)
              })}
              .
            </span>
          </div>
          <div className="bottom">
            <p className="fontBlue">{t("Thanks again for your patronage")}</p>
          </div>
        </div>
      ) : orderDetails && dateSelected ? (
        <RescheduleInner
          t={t}
          settings={settings}
          orderDetails={orderDetails}
          cartCombined={cartCombined}
          time={time}
          setTime={setTime}
          isLoading={isLoading}
          cleaner={cleaner}
          submit={submit}
          cart={[...cart, ...extras]}
          dateSelected={dateSelected}
          coupon={orderDetailsRes.data?.data["coupon code"] || null}
          cartCouponTotal={couponRes.data?.amount || null}
          couponLoading={couponRes.loading}
        />
      ) : orderDetailsRes.loading ? (
        <div style={{ textAlign: "center" }}>{t("Loading...")}</div>
      ) : (
        <div />
      )}
    </div>
  ) : (
    <></>
  );
};

const mapStateToProps = (state: stateType) => ({
  settings: state.settingsReducer
});

const ConnectedReschedule = connect(mapStateToProps)(Reschedule);

export default ConnectedReschedule;

const RescheduleButtons = ({
  smallScreen,
  disabled,
  submit,
  loading
}: {
  smallScreen?: boolean;
  disabled: boolean;
  submit: () => void;
  loading: boolean;
}) => {
  const { t } = useContext(LocaleContext);

  return (
    <div className={`${smallScreen ? `showSm` : ``}`}>
      <div className={`check_list_btn_wrapper direct reschedule`}>
        <div className="check_list_btn_inner flex">
          <Button
            type="button"
            onClick={submit}
            disabled={loading || disabled}
            size="sm"
            span
          >
            {loading ? t("Loading...") : t("Reschedule")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const RescheduleInnerComp = ({
  t,
  settings,
  orderDetails,
  cartCombined,
  time,
  setTime,
  isLoading,
  cleaner,
  submit,
  coupon,
  cartCouponTotal,
  cart,
  dateSelected,
  couponLoading,
  clearCart,
  setEntireCart
}: {
  t: (data: keyof typeof json) => string;
  settings: Partial<settings>;
  orderDetails: orderDetailsType["data"];
  cartCombined: {
    services: (c_item & {
      duration: number;
    })[];
    extra_services: (e_item & {
      duration: number;
    })[];
  };
  time: string | undefined;
  setTime: (data: string | undefined) => void;
  isLoading: boolean;
  cleaner: string;
  submit: () => void;
  coupon: string | null;
  cartCouponTotal: number | null;
  cart: order["order"]["services"];
  dateSelected: Date;
  couponLoading: boolean;
  clearCart: (ignoreAnalytics: boolean) => void;
  setEntireCart: (items: cartInitialState) => void;
}) => {
  const {
    serviceDuration,
    availableTimeRes,
    date,
    disabledDateObj,
    disabledDatesRes,
    timeArray,
    setMonth,
    setDate
  } = useTimeDetails({
    hide: false,
    longitude: orderDetails.customer.longitude,
    latitude: orderDetails.customer.latitude,
    address: orderDetails.customer.address,
    // @ts-ignore
    eircode: orderDetails.customer.eircode,
    orderDetails: parseDirectOrder(orderDetails),
    services: cartCombined.services,
    extra_services: cartCombined.extra_services
  });

  useEffect(() => {
    setDate(dateSelected);
  }, [String(dateSelected)]);

  const {
    cart: cart_items,
    extras: extra_items,
    gift_services
  } = parseToCartItem({
    cart: orderDetails.services,
    extras: orderDetails.extra_services,
    gift_values: orderDetails.gift_values
  });

  const setDefault = () => {
    clearCart(true);
  };

  useEffect(() => {
    setEntireCart({
      cart: cart_items.concat(gift_services),
      extras: extra_items
    });
  }, [JSON.stringify(orderDetails)]);

  useEffect(() => {
    return () => {
      setDefault();
    };
  }, []);

  return (
    <>
      <CheckOutSection
        Buttons={
          <RescheduleButtons
            submit={submit}
            loading={isLoading}
            disabled={
              !time ||
              !date ||
              availableTimeRes.loading ||
              !!availableTimeRes.error
            }
          />
        }
        reschedule
        availableTimeRes={availableTimeRes}
        disabledDatesRes={disabledDatesRes}
        cleaner={cleaner}
        cleanerError={!!availableTimeRes.error}
        date={date}
        disabledDateObj={disabledDateObj}
        orderDetails={orderDetails}
        serviceDuration={serviceDuration}
        setDate={setDate}
        setMonth={setMonth}
        settings={settings}
        time={time}
        timeArray={timeArray}
        time_set={(fullTime) => {
          setTime(fullTime);
        }}
      >
        <CheckOutListReschedule
          buttonJSX={
            <RescheduleButtons
              submit={submit}
              loading={isLoading}
              disabled={
                !time ||
                !date ||
                availableTimeRes.loading ||
                !!availableTimeRes.error
              }
            />
          }
          time_start={orderDetails.time_start}
          time_end={orderDetails.time_end}
          orderDetails={parseDirectOrder(orderDetails)}
          coupon={coupon}
          cart={cart_items}
          gift_services={gift_services}
          extras={extra_items}
          cartCouponTotal={cartCouponTotal}
          couponLoading={couponLoading}
        />
      </CheckOutSection>
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  clearCart: (ignoreAnalytics: boolean) => {
    dispatch(clearCart(ignoreAnalytics));
  },
  setEntireCart: (items: cartInitialState) => {
    dispatch(setEntireCart(items));
  }
});

const RescheduleInner = connect(
  undefined,
  mapDispatchToProps
)(RescheduleInnerComp);
